// React
import React, { forwardRef, cloneElement, isValidElement } from "react";
import PropTypes from "prop-types";
// Framework
import { UserInteraction } from "analytics";
import { Section } from "ui/section";
import { PiPlusCircleDuotone as AddIcon } from "@mefisto/icon";
import { makeStyles, Hidden, Fab as Component } from "@material-ui/core";

////////////////////////////////////////////////////
/// Styles
////////////////////////////////////////////////////

const useStyles = makeStyles((theme) => ({
  fab: {
    boxShadow: theme.shadows[0],
    borderRadius: `${theme.radius.normal}px !important`,
    textTransform: "capitalize",
    color: theme.palette.action.active,
    border: theme.palette.border.prominent.main,
    backgroundColor: theme.palette.background.prominent.main,
    "&:hover": {
      backgroundColor: theme.palette.background.prominent.hover,
    },
    "&:active": {
      boxShadow: theme.shadows[0],
    },
    "&$focusVisible": {
      boxShadow: theme.shadows[0],
    },
  },
  focusVisible: {},
  label: {
    whiteSpace: "nowrap",
    [theme.breakpoints.up("sm")]: {
      marginRight: theme.spacing(1),
    },
  },
  icon: {
    [theme.breakpoints.up("sm")]: {
      marginRight: theme.spacing(1),
    },
  },
}));

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const AddButton = forwardRef(
  (
    { context = "button", value, icon = <AddIcon />, onClick, children },
    ref
  ) => {
    // Styles
    const classes = useStyles();
    // Render
    return (
      <UserInteraction
        ref={ref}
        element="button"
        value={value}
        component={Component}
        componentProps={{
          variant: "extended",
          size: "small",
          classes: {
            root: classes.fab,
            label: classes.label,
            focusVisible: classes.focusVisible,
          },
          onClick,
        }}
      >
        <Section context={context} value={value}>
          {isValidElement(icon) &&
            cloneElement(icon, {
              className: classes.icon,
            })}
          <Hidden xsDown>{children}</Hidden>
        </Section>
      </UserInteraction>
    );
  }
);

AddButton.propTypes = {
  /**
   * Section context name
   */
  context: PropTypes.string,
  /**
   * Element specification value
   */
  value: PropTypes.string,
  /**
   * Button icon
   */
  icon: PropTypes.element,
  /**
   * The content of the add button.
   */
  children: PropTypes.node,
};

export default AddButton;
